import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  bigquerySearchLike,
  BIGQUERY_SEARCH_LIKE_DATASET_NAME,
  BIGQUERY_SEARCH_LIKE_TABLE_NAME,
} from "../../api/searchLike";
import { useCmmInputUniqueValuesContext } from "../../context/cmmInputUniqueValuesContext";
import type { CmmInputUniqueValueItem } from "../../pages/configurations/pages/validationFunnel/pages/conditionList/cmmInputUniqueValuesTypes";

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 10;
const CREATED_AT_CUTOFF_MONTHS = 3;

export interface QueryBuilderValueSearchOption {
  id: string;
  label: string;
}

export interface UseQueryBuilderValueSearchParams {
  /** Column/field name used for BigQuery search (select_column and filter_column). */
  columnName: string;
  /** Initial options shown when dropdown opens (e.g. from config top_values). */
  initialOptions: QueryBuilderValueSearchOption[];
  /** Reset when column or operator changes (e.g. condition.field, condition.operator). */
  resetDeps?: unknown[];
  /**
   * When true, exposes the typed search query as a selectable option when BigQuery
   * returns no matches (used by rule-modal action value fields).
   */
  allowCustomValueOnEmptySearch?: boolean;
}

export interface QueryBuilderValueSearchProps {
  options: QueryBuilderValueSearchOption[];
  onSearch: (query: string) => Promise<QueryBuilderValueSearchOption[]>;
  onOpenChange: (open: boolean) => void;
  searchPlaceholder: string;
  searchMinChars: number;
  searchDebounceMs: number;
  searchNoResultsMessage: string;
  allowCustomValueOnEmptySearch?: boolean;
  onFooterApply: (selectedIds: string[]) => void;
  footerPrimaryLabel: string;
  hideFooter: boolean;
}

function stripLabelCount(label: string): string {
  return label.replace(/\s*\(\d+\)$/, "");
}

function cleanOptions(
  opts: QueryBuilderValueSearchOption[],
): QueryBuilderValueSearchOption[] {
  return opts.map((o) => ({ ...o, label: stripLabelCount(o.label) }));
}

/**
 * Hook for async value search in QueryBuilder condition rows.
 * Uses the same BigQuery search API as ConditionListTopValuesModal (bigquerySearchLike).
 * Returns props to spread onto MultiSelectWithTag for search-as-you-type behavior.
 */
function withCustomSearchOption(
  options: QueryBuilderValueSearchOption[],
  query: string,
): QueryBuilderValueSearchOption[] {
  const trimmed = query.trim();
  if (!trimmed) return options;
  if (options.some((o) => o.id.toLowerCase() === trimmed.toLowerCase())) {
    return options;
  }
  return [{ id: trimmed, label: trimmed }, ...options];
}

export function useQueryBuilderValueSearch({
  columnName,
  initialOptions: rawInitialOptions,
  resetDeps = [],
  allowCustomValueOnEmptySearch = false,
}: UseQueryBuilderValueSearchParams): QueryBuilderValueSearchProps {
  const initialOptions = cleanOptions(rawInitialOptions);
  const [dropdownOptions, setDropdownOptions] = useState(initialOptions);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const initialOptionsRef = useRef(initialOptions);
  initialOptionsRef.current = initialOptions;

  useEffect(() => {
    setDropdownOptions(initialOptions);
  }, [columnName, ...resetDeps]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const handleSearch = useCallback(
    (query: string): Promise<QueryBuilderValueSearchOption[]> => {
      const q = (query ?? "").trim();
      if (q.length < MIN_SEARCH_LENGTH) {
        setDropdownOptions(initialOptions);
        return Promise.resolve(initialOptions);
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
      return new Promise((resolve) => {
        debounceRef.current = setTimeout(() => {
          debounceRef.current = null;
          abortRef.current = new AbortController();
          const signal = abortRef.current.signal;
          bigquerySearchLike(
            {
              distinct: true,
              select_column: columnName,
              filter_column: columnName,
              query: q,
              limit: SEARCH_LIMIT,
              created_at_cutoff_months: CREATED_AT_CUTOFF_MONTHS,
              dataset_name: BIGQUERY_SEARCH_LIKE_DATASET_NAME,
              table_name: BIGQUERY_SEARCH_LIKE_TABLE_NAME,
            },
            signal,
          )
            .then((results) => {
              let mapped: QueryBuilderValueSearchOption[] = (
                results ?? []
              ).map((text) => ({ id: text, label: stripLabelCount(text) }));

              if (allowCustomValueOnEmptySearch && mapped.length === 0) {
                mapped = withCustomSearchOption(mapped, q);
              }

              const mergedMap = new Map<
                string,
                QueryBuilderValueSearchOption
              >();
              for (const o of mapped) mergedMap.set(o.id, o);
              for (const o of initialOptionsRef.current) {
                if (!mergedMap.has(o.id)) mergedMap.set(o.id, o);
              }
              const merged = Array.from(mergedMap.values());

              setDropdownOptions(merged);
              resolve(merged);
            })
            .catch((err) => {
              if ((err as Error).name !== "AbortError") {
                const fallback =
                  allowCustomValueOnEmptySearch && q
                    ? withCustomSearchOption([], q)
                    : [];
                setDropdownOptions(fallback);
                resolve(fallback);
              } else {
                resolve([]);
              }
            })
            .finally(() => {
              abortRef.current = null;
            });
        }, SEARCH_DEBOUNCE_MS);
      });
    },
    [allowCustomValueOnEmptySearch, columnName, initialOptions],
  );

  const handleDropdownOpenChange = useCallback((open: boolean) => {
    if (!open) setDropdownOptions(initialOptionsRef.current);
  }, []);

  const { cmmInputUniqueValues, saveTopValues } =
    useCmmInputUniqueValuesContext();

  const handleFooterApply = useCallback(
    async (selectedIds: string[]) => {
      if (!columnName) return;
      const column = cmmInputUniqueValues?.columns?.find(
        (c) => c.column_name === columnName,
      );
      if (!column) return;

      const existingValueSet = new Set(
        (column.top_values ?? []).map((t) => {
          if (t != null && typeof t === "object" && "value" in t) {
            return String((t as CmmInputUniqueValueItem).value);
          }
          return String(t);
        }),
      );

      const newValues = selectedIds.filter((id) => !existingValueSet.has(id));
      if (newValues.length === 0) return;

      const mergedTopValues: CmmInputUniqueValueItem[] = (
        column.top_values ?? []
      ).map((t) => {
        if (t != null && typeof t === "object" && "value" in t) {
          return t as CmmInputUniqueValueItem;
        }
        return { value: t, count: 1 };
      });
      for (const val of newValues) {
        mergedTopValues.push({ value: val, count: 1 });
      }

      try {
        await saveTopValues(columnName, mergedTopValues);
        toast.success("Top values updated.");
      } catch {
        toast.error("Failed to save top values.");
      }
    },
    [columnName, cmmInputUniqueValues, saveTopValues],
  );

  return {
    options: dropdownOptions,
    onSearch: handleSearch,
    onOpenChange: handleDropdownOpenChange,
    searchPlaceholder: `Type ${MIN_SEARCH_LENGTH}+ characters to search...`,
    searchMinChars: MIN_SEARCH_LENGTH,
    searchDebounceMs: SEARCH_DEBOUNCE_MS,
    searchNoResultsMessage: "No results found",
    allowCustomValueOnEmptySearch,
    onFooterApply: handleFooterApply,
    footerPrimaryLabel: "Save",
    hideFooter: false,
  };
}
